import { Material, StockControlMode } from '../../../types';

const FIXED_EXPENSE_KEYWORDS = [
  'folha de pagamento',
  'folha salarial',
  'folha de pagto',
  'folha pagto',
  'folha',
  'pagto',
  'pagto funcionario',
  'pagto funcionários',
  'pagamento de funcionario',
  'pagamento de funcionários',
  'salario',
  'salário',
  'salarios',
  'salários',
  'pro-labore',
  'pró-labore',
  'pro labore',
  'encargos trabalhistas',
  'fgts',
  'inss',
  'vale transporte',
  'aluguel',
  'condominio',
  'condomínio',
  'iptu',
  'internet e celular',
  'mei (das)',
  'das mei',
  'dominio e hospedagem',
  'domínio e hospedagem',
  'anuidade de classe',
  'software e saas',
  'mensalidade sistema',
  'limpeza e conservacao',
  'limpeza e conservação',
  'seguranca',
  'segurança',
  'reserva de depreciacao',
  'reserva de depreciação',
  'contador',
  'contabilidade',
  'honorarios contabeis',
  'honorários contábeis',
  'energia e agua',
  'energia e água',
  'conta de luz',
  'conta de agua',
  'conta de água',
  'transporte e combustivel',
  'transporte e combustível',
  'manutencao de equipamentos',
  'manutenção de equipamentos',
  'educacao e cursos',
  'educação e cursos',
  'marketing (ads)',
  'trafego pago',
  'tráfego pago'
];

const UNIT_SALE_KEYWORDS = [
  'agua',
  'água',
  'picole',
  'picolé',
  'refrigerante',
  'coca',
  'guarana',
  'guaraná',
  'suco',
  'energetico',
  'energético',
  'cerveja',
  'lata',
  'garrafa',
  'fardo',
  'paleta',
  'moreninha',
  'skimo',
  'bombom',
  'trufa',
  'salgado',
  'coxinha',
  'empada',
  'pao de queijo',
  'pão de queijo'
];

function normalizeText(value?: string): string {
  if (!value) return '';
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Identifica se um registro de Material é na verdade uma Despesa Fixa / Folha de Pagamento / Estrutura
 * que NÃO deve aparecer no Estoque Físico (potes, picolés, águas, insumos).
 */
export function isFixedExpenseMaterial(m?: Partial<Material> | null): boolean {
  if (!m) return false;
  if (m.category === 'fixo' || m.itemType === 'custo_fixo') {
    return true;
  }
  if (m.depreciationMonths && m.depreciationMonths > 0) {
    return true;
  }
  const unitNorm = normalizeText(m.unit);
  if (unitNorm === 'mes' || unitNorm === 'mês' || unitNorm === 'mensal') {
    return true;
  }
  const nameNorm = normalizeText(m.name);
  if (!nameNorm) return false;
  return FIXED_EXPENSE_KEYWORDS.some(kw => {
    const kwNorm = normalizeText(kw);
    return nameNorm === kwNorm || nameNorm.includes(kwNorm);
  });
}

/**
 * Retorna true apenas para itens físicos estocáveis (Potes de Sorvete, Açaí, Caldas, Água, Picolés, Embalagens, Mercadorias).
 */
export function isPhysicalStockMaterial(m?: Partial<Material> | null): boolean {
  if (!m) return false;
  return !isFixedExpenseMaterial(m);
}

/**
 * Filtra e retorna apenas os itens de estoque físico de uma lista de materiais.
 */
export function filterPhysicalMaterials<T extends Partial<Material>>(materials: T[]): T[] {
  return materials.filter(m => isPhysicalStockMaterial(m));
}

/**
 * Filtra e retorna apenas despesas fixas e folha de pagamento de uma lista de materiais.
 */
export function filterFixedExpenseMaterials<T extends Partial<Material>>(materials: T[]): T[] {
  return materials.filter(m => isFixedExpenseMaterial(m));
}

/**
 * Resolve o modo de controle/baixa do item:
 * - 'pot_open': Pote / Caixa de Buffet ou Insumo de consumo gradual (dá baixa quando abre o pote/caixa no estoque).
 * - 'unit_sale': Água, Picolé, Bebida ou Item Unitário (venda direta por unidade no PDV ou entrada/baixa por fardo/unidade).
 */
export function resolveStockControlMode(m?: Partial<Material> | null): StockControlMode {
  if (!m) return 'pot_open';
  if (m.stockControlMode === 'pot_open' || m.stockControlMode === 'unit_sale') {
    return m.stockControlMode;
  }
  const metaMode = m.nicheMetadata?.stockControlMode;
  if (metaMode === 'pot_open' || metaMode === 'unit_sale') {
    return metaMode;
  }
  const nameNorm = normalizeText(m.name);
  if (UNIT_SALE_KEYWORDS.some(kw => nameNorm.includes(normalizeText(kw)))) {
    return 'unit_sale';
  }
  if (m.itemType === 'produto_venda') {
    // Se for pote/caixa de buffet (ex: 5L, 10L, balde), mantém pot_open; senão unit_sale
    if (nameNorm.includes('pote ') || nameNorm.includes('caixa 10l') || nameNorm.includes('caixa 5l') || nameNorm.includes('balde') || nameNorm.includes('buffet')) {
      return 'pot_open';
    }
    return 'unit_sale';
  }
  return 'pot_open';
}
